import { mockTransactions } from '@/data/mockTransactions';
import {
  StatusType,
  Transaction,
  TransactionDocument,
  TransactionWithDocuments,
} from '@/types/transaction';
import {
  TransactionStatus,
  TransactionStatusLog,
  TransactionStatusLogResponse,
} from '@/types/transactionStatusLog';

// Mock transaction documents
const mockDocuments: TransactionDocument[] = [
  {
    id: 'doc-001',
    name: 'invoice.pdf',
    mimeType: 'application/pdf',
    size: 245678,
    uploadDate: '2025-01-14T10:30:00Z',
    viewUrl: '/api/v1/documents/doc-001/view',
  },
];

// Function to create mock transaction details with documents
export const createMockTransactionDetails = (
  transactionId: string,
): TransactionWithDocuments | null => {
  // Find the transaction in our mock data
  const transaction = mockTransactions.find((t: Transaction): boolean => t.id === transactionId);

  if (!transaction) {
    return null;
  }

  // Add documents to some transactions (not all)
  const hasDocuments = Math.random() > 0.4; // 60% chance of having documents
  const documents = hasDocuments
    ? mockDocuments.slice(0, Math.floor(Math.random() * 3) + 1) // 1-3 documents
    : [];

  return {
    ...transaction,
    documents,
  };
};

// Mock transaction status logs data
const mockStatusLogs: Record<string, TransactionStatusLogResponse> = {
  'TXN-001': {
    statusLogs: [
      {
        status: TransactionStatus.CREATED,
        provider: null,
        internal: true,
        final: false,
        createdAt: '2025-01-14T10:28:00Z',
      },
      {
        status: TransactionStatus.PENDING,
        provider: 'Chase Bank',
        internal: false,
        final: false,
        createdAt: '2025-01-14T10:29:00Z',
      },
      {
        status: TransactionStatus.PROCESSING,
        provider: 'Chase Bank',
        internal: false,
        final: false,
        createdAt: '2025-01-14T10:30:00Z',
      },
      {
        status: TransactionStatus.SUCCEEDED,
        provider: 'Chase Bank',
        internal: false,
        final: true,
        createdAt: '2025-01-14T10:35:00Z',
      },
    ],
    nextStatus: [], // Final status, no next steps
  },
  'TXN-002': {
    statusLogs: [
      {
        status: TransactionStatus.CREATED,
        provider: null,
        internal: true,
        final: false,
        createdAt: '2025-01-13T15:43:00Z',
      },
      {
        status: TransactionStatus.PENDING,
        provider: 'ACH Network',
        internal: false,
        final: false,
        createdAt: '2025-01-13T15:45:00Z',
      },
    ],
    // For a deposit in PENDING, realistically it will PROCESS, then mark DEPOSIT_COMPLETED and finally SUCCEEDED
    nextStatus: ['PROCESSING', 'DEPOSIT_COMPLETED', 'SUCCEEDED'],
  },
  'TXN-003': {
    statusLogs: [
      {
        status: TransactionStatus.CREATED,
        provider: null,
        internal: true,
        final: false,
        createdAt: '2025-01-12T09:18:00Z',
      },
      {
        status: TransactionStatus.PENDING,
        provider: 'Wire Transfer Network',
        internal: false,
        final: false,
        createdAt: '2025-01-12T09:20:00Z',
      },
      {
        status: TransactionStatus.PROCESSING,
        provider: 'Wire Transfer Network',
        internal: false,
        final: false,
        createdAt: '2025-01-12T09:22:00Z',
      },
      {
        status: TransactionStatus.FAILED,
        provider: 'Wire Transfer Network',
        internal: false,
        final: true,
        createdAt: '2025-01-12T09:25:00Z',
      },
    ],
    nextStatus: [], // Failed - no next steps
  },
  'TXN-004': {
    statusLogs: [
      {
        status: TransactionStatus.CREATED,
        provider: null,
        internal: true,
        final: false,
        createdAt: '2025-01-11T14:08:00Z',
      },
      {
        status: TransactionStatus.PENDING,
        provider: 'Currency Exchange Service',
        internal: false,
        final: false,
        createdAt: '2025-01-11T14:09:00Z',
      },
      {
        status: TransactionStatus.PROCESSING,
        provider: 'Currency Exchange Service',
        internal: false,
        final: false,
        createdAt: '2025-01-11T14:10:00Z',
      },
      {
        status: TransactionStatus.EXCHANGE_COMPLETED,
        provider: 'Currency Exchange Service',
        internal: false,
        final: false,
        createdAt: '2025-01-11T14:12:00Z',
      },
      {
        status: TransactionStatus.SUCCEEDED,
        provider: 'Currency Exchange Service',
        internal: false,
        final: true,
        createdAt: '2025-01-11T14:15:00Z',
      },
    ],
    nextStatus: [], // Completed
  },
  'TXN-005': {
    statusLogs: [
      {
        status: TransactionStatus.CREATED,
        provider: null,
        internal: true,
        final: false,
        createdAt: '2025-01-10T11:28:00Z',
      },
      {
        status: TransactionStatus.PENDING,
        provider: 'ACH Network',
        internal: false,
        final: false,
        createdAt: '2025-01-10T11:30:00Z',
      },
      {
        status: TransactionStatus.WAITING_WEBHOOK,
        provider: 'Wells Fargo',
        internal: false,
        final: false,
        createdAt: '2025-01-10T11:32:00Z',
      },
    ],
    // For a transfer waiting on webhook, expect PROCESSING then PAYMENT_COMPLETED then SUCCEEDED
    nextStatus: ['PROCESSING', 'PAYMENT_COMPLETED', 'SUCCEEDED'],
  },
};

// Function to generate status logs for any transaction
const generateDefaultStatusLogs = (transactionId: string): TransactionStatusLogResponse => {
  const transaction = mockTransactions.find((t: Transaction): boolean => t.id === transactionId);

  if (!transaction) {
    return {
      statusLogs: [],
      nextStatus: [],
    };
  }

  const baseDate = new Date(transaction.date);
  const statusLogs: TransactionStatusLog[] = [
    {
      status: TransactionStatus.CREATED,
      provider: null,
      internal: true,
      final: false,
      createdAt: new Date(baseDate.getTime() - 2 * 60 * 1000).toISOString(), // 2 minutes before
    },
  ];

  // Add intermediate status based on transaction status
  if (transaction.status !== StatusType.CREATED) {
    statusLogs.push({
      status: TransactionStatus.PENDING,
      provider: 'Payment Provider',
      internal: false,
      final: false,
      createdAt: new Date(baseDate.getTime() - 60 * 1000).toISOString(), // 1 minute before
    });
  }

  // Add final status
  const isFinalStatus = [
    StatusType.SUCCEEDED,
    StatusType.FAILED,
    StatusType.EXPIRED,
    StatusType.CANCELLED,
    StatusType.REJECTED,
  ].includes(transaction.status);

  if (isFinalStatus) {
    // Map transaction status to TransactionStatus enum if it exists, otherwise use as string
    const mappedStatus =
      transaction.status === StatusType.SUCCEEDED
        ? TransactionStatus.SUCCEEDED
        : transaction.status === StatusType.FAILED
          ? TransactionStatus.FAILED
          : (transaction.status as any);
    statusLogs.push({
      status: mappedStatus,
      provider: 'Payment Provider',
      internal: false,
      final: true,
      createdAt: transaction.date,
    });
  } else {
    // Map transaction status to TransactionStatus enum if it exists, otherwise use as string
    const mappedStatus =
      transaction.status === StatusType.PENDING
        ? TransactionStatus.PENDING
        : transaction.status === StatusType.WAITING_WEBHOOK
          ? TransactionStatus.WAITING_WEBHOOK
          : (transaction.status as any);
    statusLogs.push({
      status: mappedStatus,
      provider: 'Payment Provider',
      internal: false,
      final: false,
      createdAt: transaction.date,
    });
  }

  // Determine realistic next statuses based on current mapped status and transaction type
  const lastLog = statusLogs[statusLogs.length - 1];
  let nextStatus: string[] = [];

  if (!isFinalStatus) {
    switch (lastLog.status) {
      case TransactionStatus.CREATED:
        nextStatus = [TransactionStatus.PENDING, TransactionStatus.PROCESSING];
        break;
      case TransactionStatus.PENDING:
        nextStatus = [TransactionStatus.PROCESSING];
        break;
      case TransactionStatus.WAITING_WEBHOOK:
        nextStatus = [TransactionStatus.PROCESSING];
        break;
      case TransactionStatus.PROCESSING:
        nextStatus = [];
        break;
      default:
        nextStatus = [];
    }

    // Append type-specific completion step before SUCCEEDED when applicable
    if (transaction.type === ('EXCHANGE' as any)) {
      // If we haven't yet marked exchange completion, show it next
      if (
        !statusLogs.some(
          (s: TransactionStatusLog): boolean => s.status === TransactionStatus.EXCHANGE_COMPLETED,
        )
      ) {
        nextStatus.push(TransactionStatus.EXCHANGE_COMPLETED);
      }
    } else if (transaction.type === ('DEPOSIT' as any)) {
      if (
        !statusLogs.some(
          (s: TransactionStatusLog): boolean => s.status === TransactionStatus.DEPOSIT_COMPLETED,
        )
      ) {
        nextStatus.push(TransactionStatus.DEPOSIT_COMPLETED);
      }
    } else if (
      transaction.type === ('TRANSFER' as any) ||
      transaction.type === ('WITHDRAWAL' as any)
    ) {
      if (
        !statusLogs.some(
          (s: TransactionStatusLog): boolean => s.status === TransactionStatus.PAYMENT_COMPLETED,
        )
      ) {
        nextStatus.push(TransactionStatus.PAYMENT_COMPLETED);
      }
    }

    // Final expected success state
    nextStatus.push(TransactionStatus.SUCCEEDED);
  }

  return {
    statusLogs,
    nextStatus,
  };
};

export const getMockTransactionStatusLogs = (
  transactionId: string,
): TransactionStatusLogResponse => {
  // Return predefined mock data if available, otherwise generate default
  return mockStatusLogs[transactionId] || generateDefaultStatusLogs(transactionId);
};
